import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

/** One entry from the page-help index (used to decide which pages show a help icon). */
export interface PageHelpIndexEntry {
  route: string;
  title: string;
  screenshot_url: string | null;
}

/** A full Knowledge Base article for a page. */
export interface PageHelpArticle extends PageHelpIndexEntry {
  slug: string;
  summary: string;
  body_markdown: string;
  updated_at: number;
}

/** Fetch (and cache) the list of routes that have a "How to use this page" article. */
export function usePageHelpIndex() {
  return useQuery({
    queryKey: ['page-help-index'],
    queryFn: async () =>
      (await apiClient.get<PageHelpIndexEntry[]>('/api/support/page-help/index/')).data,
    staleTime: 1000 * 60 * 30,
  });
}

/**
 * Resolve a pathname to the best article route: exact match, else the longest
 * route that is a path-segment prefix (so detail pages fall back to the section
 * article). Returns null when no page has help.
 */
export function matchHelpRoute(pathname: string, index?: PageHelpIndexEntry[]): string | null {
  if (!index || index.length === 0) return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  let best: string | null = null;
  let bestScore = -1;
  const actual = path.split('/').filter(Boolean);
  for (const entry of index) {
    const route = entry.route;
    if (path === route) return route;
    const parts = route.split('/').filter(Boolean);
    const matches = parts.length > 0 && parts.length <= actual.length && parts.every((part, i) => part.startsWith(':') || part === actual[i]);
    const score = parts.length * 100 + parts.filter(part => !part.startsWith(':')).length;
    if (matches && score > bestScore) { best = route; bestScore = score; }
  }
  return best;
}
